import React, { useState } from "react";

import { TrainingAPI } from "../../api/TrainingAPI";
import { useSelector } from "react-redux";

import "bootstrap/dist/css/bootstrap.min.css";
import { Select, InputNumber, Button, Tooltip } from "antd";
import { audioMessage as message } from "../Common/Notifications/audioMessage";

import "../../Styles/styles-PostData.css";
import "../../Styles/styles-Settings.css";
import { BaselineTable } from "../Baseline/BaselineTable";
import { formattedPlayerName } from "../../Utils/name_utils";
import { PositionSelect } from "../PositionSelect/PositionSelect";
import { displayValidationErrorMessages } from "./TeamSettings";
import { 
  PermissionedSelect, 
  PermissionedInputNumber, 
  PermissionedButton,
  PermissionedPositionSelect
} from "../PermissionedComponents";
import PermissionViewable from "../PermissionedComponents/PermissionViewable";

const { Option, OptGroup } = Select;

export const PlayerBaseline = ({
  possiblePositions,
  sport,
  baselineOverviewData,
  getBaselineOverviewData,
}) => {
  const trainingAPI = new TrainingAPI();

  const {
    info: { display_name: teamName },
    players,
    } = useSelector(({ team }) => team);
  const [playerId, setPlayerId] = useState(null);
  const [playerTopSpeed, setPlayerTopSpeed] = useState(0);
  const [playerTrimpMin, setPlayerTrimpMin] = useState(0);
  const [playerTrimp, setPlayerTrimp] = useState(0);
  const [playerMaxHR, setPlayerMaxHR] = useState(0);
  const [playerBodyweightBaseline, setPlayerBodyweightBaseline] = useState(0);
  const [playerPosition, setPlayerPosition] = useState(undefined);
  const [playerNumber, setPlayerNumber] = useState(0);
  const [playerSelected, setPlayerSelected] = useState(undefined);

  const newPlayerSelection = (value) => {
    setPlayerSelected(value);
    trainingAPI.getPlayer(undefined, undefined, value).then((data) => {
      setPlayerId(data.player_id);
      setPlayerNumber(data.number);
      setPlayerPosition(data.position);
      setPlayerTopSpeed(data.top_speed);
      setPlayerTrimpMin(data.trimp_per_minute);
      setPlayerTrimp(data.trimp);
      setPlayerMaxHR(data.max_hr);
      setPlayerBodyweightBaseline(data.bodyweight_baseline);
    });
  };

  const onPositionChange = (value) => {
    setPlayerPosition(value);
  };

  const onTopSpeedChange = (value) => {
    setPlayerTopSpeed(value);
  };

  const onTrimpMinChange = (value) => {
    setPlayerTrimpMin(value);
  };

  const onTrimpChange = (value) => {
    setPlayerTrimp(value);
  };

  const onMaxHRChange = (value) => {
    setPlayerMaxHR(value);
  };

  const onBodyweightBaselineChange = (value) => {
    setPlayerBodyweightBaseline(value);
  };

  const updatePlayer = async () => {
    if (playerTopSpeed == 0){
      message.error("Top Speed cannot be 0")
      return
    }

    try {
      await trainingAPI.updatePlayer(
        playerId,
        playerNumber,
        playerPosition,
        playerTopSpeed,
        playerTrimpMin,
        playerTrimp,
        playerMaxHR,
        playerBodyweightBaseline
      )
      message.success("Baseline values updated");
      getBaselineOverviewData();
    } catch (error) {
      console.log(error)
      message.error("Error updating player baselines: ", error)
    }

    try {
      const errorMessages = await trainingAPI.validateTeam();
      if(errorMessages && Array.isArray(errorMessages)) {
        displayValidationErrorMessages(errorMessages)
      }
    } catch (error) {
      message.error("Error performing team validation");
    }
  };

  return (
    <PermissionViewable 
      permissionCheck="ViewPlayerBaselines"
      permissionMessage="You don't have permission to view player baselines"
    >
      <div style={{ margin: "24px 0px" }}>
        <div className="player-baselines-grid">
          <div className="setting-baseline-item">
            <PermissionedSelect
              showSearch
              style={{ width: 200 }}
              placeholder="Choose Player"
              value={playerSelected}
              onChange={newPlayerSelection}
              permissionCheck="EditPlayerBaselines"
              filterOption={(input, option) => {
                console.log({ input, option });
                return (
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                );
              }}
            >
              {players.map((player, i) => {
                return (
                  <Option key={i} value={player.player_id}>
                    {`${formattedPlayerName(
                      teamName,
                      `${player.first_name} ${player.last_name}`
                    )} #${player.number}`}
                  </Option>
                );
              })}
            </PermissionedSelect>
          </div>
          <div className="setting-baseline-item">
            <PermissionedPositionSelect
              value={playerPosition}
              handleChange={onPositionChange}
              width={200}
              options={possiblePositions}
              sport={sport}
              permissionCheck="EditPlayerBaselines"
            />
          </div>
          <div className="set-baselines-buttons">
            <label htmlFor="top_speed">Top Speed</label>
            <br />
            <PermissionedInputNumber
              type="number"
              min="0"
              step=".1"
              id="top_speed"
              name="top_speed"
              value={playerTopSpeed}
              onChange={onTopSpeedChange}
              permissionCheck="EditPlayerBaselines"
            />
          </div>
          <div className="set-baselines-buttons">
            <label htmlFor="trimp_min">Trimp/Min</label>
            <br />
            <PermissionedInputNumber
              type="number"
              min="0"
              step=".1"
              id="trimp_min"
              name="trimp_min"
              value={playerTrimpMin}
              onChange={onTrimpMinChange}
              permissionCheck="EditPlayerBaselines"
            />
          </div>
          <div className="set-baselines-buttons">
            <label htmlFor="trimp">Trimp</label>
            <br />
            <PermissionedInputNumber
              type="number"
              min="0"
              step="1"
              id="trimp"
              name="trimp"
              value={playerTrimp}
              onChange={onTrimpChange}
              permissionCheck="EditPlayerBaselines"
            />
          </div>
          <div className="set-baselines-buttons">
            <label htmlFor="max_hr">Max HR</label>
            <br />
            <PermissionedInputNumber
              type="number"
              min="0"
              step="1"
              id="max_hr"
              name="max_hr"
              value={playerMaxHR}
              onChange={onMaxHRChange}
              permissionCheck="EditPlayerBaselines"
            />
          </div>
          <div className="set-baselines-buttons">
            <label htmlFor="bodyweight_baseline">Bodyweight</label>
            <br />
            <PermissionedInputNumber
              type="number"
              min="0"
              step="1"
              id="bodyweight_baseline"
              name="bodyweight_baseline"
              value={playerBodyweightBaseline}
              onChange={onBodyweightBaselineChange}
              permissionCheck="EditPlayerBaselines"
            />
          </div>
          <div className="setting-baseline-item">
            {playerSelected === undefined ? (
              <Tooltip title="No player selected">
                <PermissionedButton 
                  type="primary" 
                  disabled
                  permissionCheck="EditPlayerBaselines"
                >
                  Update
                </PermissionedButton>
              </Tooltip>
            ) : (
              <PermissionedButton 
                type="primary" 
                onClick={updatePlayer}
                permissionCheck="EditPlayerBaselines"
              >
                Update
              </PermissionedButton>
            )}
          </div>
        </div>

        <BaselineTable data={baselineOverviewData} />
      </div>
    </PermissionViewable>
  );
};
